/**
 * The shop check: where the storefronts, as the latest reading of each saw
 * them, and the inventory disagree. Read-only; each row links to where it's
 * put right (the item page, the review list on Sync shops, the storefront).
 */
import { useState } from "react";
import { Link } from "react-router";
import { SHOP_DISCREPANCY_KINDS, type ShopDiscrepancy } from "@sable/api-contract";
import { IconBox } from "../components/icons.tsx";
import { ErrorBlock, Loading, StatusPill } from "../components/ui.tsx";
import { t, tk } from "../i18n/index.ts";
import { money, shortDate } from "../lib/format.ts";
import { useMarketplaces, useShopReport } from "../lib/queries.ts";
import { useWs, wsPath } from "../lib/workspace.tsx";

type Kind = (typeof SHOP_DISCREPANCY_KINDS)[number];

function Thumb({ src }: { src: string | null }) {
  const [broken, setBroken] = useState(false);
  return (
    <div className="mini">
      {src && !broken
        ? <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setBroken(true)} />
        : <div className="thumb-empty"><IconBox /></div>}
    </div>
  );
}

function Row({ row, mpName }: { row: ShopDiscrepancy; mpName: (code: string) => string }) {
  const ws = useWs();
  const site = mpName(row.marketplace);
  const { seen, listing, item } = row;
  const storeUrl = seen?.url ?? listing?.url ?? null;

  return (
    <div className="shop-row">
      <div className="report-head">
        <span className={`pill report-kind report-${row.kind}`}>{tk("shopReport.kind", row.kind)}</span>
        <span className="small">
          {t(`shopReport.explain.${row.kind}` as "shopReport.explain.not_in_inventory", { site, other: row.soldOn ? mpName(row.soldOn.marketplace) : "" })}
        </span>
      </div>

      <div className="report-sides">
        <div className="report-side">
          <div className="muted small">{t("shopReport.storefront", { site })}</div>
          {seen ? (
            <div className="shop-seen">
              <Thumb src={seen.photoUrl} />
              <div className="grow">
                <div className="name">{seen.title || row.externalId}</div>
                <div className="sub">
                  <span className={`pill pill-${seen.status === "inactive" ? "ended" : seen.status}`}>{tk("shopReport.seenStatus", seen.status)}</span>
                  {seen.priceMinor !== null && <> · {money(seen.priceMinor, seen.currency)}</>}
                </div>
              </div>
            </div>
          ) : <div className="sub">{t("shopReport.notShown")}</div>}
        </div>

        <div className="report-side">
          <div className="muted small">{t("shopReport.ours")}</div>
          {item ? (
            <div className="shop-seen">
              <Thumb src={item.thumbUrl} />
              <div className="grow">
                <Link to={wsPath(ws, `/items/${item.id}`)} className="name">{item.title || t("inventory.untitled")}</Link>
                <div className="sub">
                  {item.sku} · <StatusPill status={item.status} />
                </div>
                <div className="sub">
                  {listing
                    ? <>
                        {site}: <StatusPill status={listing.status} kind="listingStatus" />
                        {listing.priceMinor !== null && <> · {money(listing.priceMinor, listing.currency)}</>}
                        {listing.accountHandle && <> · {t("shopReport.account", { handle: listing.accountHandle })}</>}
                      </>
                    : t("shopReport.noListing")}
                </div>
              </div>
            </div>
          ) : <div className="sub">{t("shopReport.noItem")}</div>}
        </div>
      </div>

      <div className="row-wrap">
        {item && <Link className="btn btn-sm" to={wsPath(ws, `/items/${item.id}`)}>{t("shopReport.openItem")}</Link>}
        {row.kind === "not_in_inventory" && <Link className="btn btn-sm btn-primary" to={wsPath(ws, "/shops")}>{t("shopReport.review")}</Link>}
        {storeUrl && (
          <a className="btn btn-sm btn-ghost" href={storeUrl} target="_blank" rel="noreferrer noopener">{t("shopReport.openStorefront", { site })}</a>
        )}
        {row.soldOn?.url && (
          <a className="btn btn-sm btn-ghost" href={row.soldOn.url} target="_blank" rel="noreferrer noopener">
            {t("shopReport.openStorefront", { site: mpName(row.soldOn.marketplace) })}
          </a>
        )}
      </div>
    </div>
  );
}

export function ShopReport() {
  const ws = useWs();
  const mps = useMarketplaces();
  const q = useShopReport(ws.id);
  const [kind, setKind] = useState<Kind | "all">("all");
  const [mp, setMp] = useState<string>("all");
  const mpName = (code: string) => mps.data?.find((m) => m.code === code)?.name ?? code;

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorBlock error={q.error} onRetry={() => void q.refetch()} />;
  const { marketplaces, rows } = q.data;
  const inShop = rows.filter((r) => mp === "all" || r.marketplace === mp);
  const shown = inShop.filter((r) => kind === "all" || r.kind === kind);
  const countOf = (k: Kind) => inShop.filter((r) => r.kind === k).length;

  return (
    <div className="stack-lg">
      <div className="page-head">
        <div>
          <h1>{t("shopReport.title")}</h1>
          <div className="sub">{t("shopReport.hint")}</div>
        </div>
        <Link className="btn btn-ghost" to={wsPath(ws, "/shops")}>{t("shopReport.back")}</Link>
      </div>

      <section className="card stack">
        <div className="list">
          {marketplaces.map((m) => (
            <div className="list-row" key={m.code}>
              <div className="grow">
                <div className="name">{mpName(m.code)}</div>
                <div className="sub">
                  {m.readAt
                    ? <>
                        {t("shopReport.readAt", { when: shortDate(m.readAt) })} · {t("shopReport.seenCount", { count: m.seen })}
                        {!m.complete && <> · <span className="neg">{t("shopReport.partial")}</span></>}
                      </>
                    : t("shopReport.notRead")}
                </div>
              </div>
              <div className="name">{rows.filter((r) => r.marketplace === m.code).length}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="card stack">
        {marketplaces.length > 1 && (
          <div className="chips" role="group" aria-label={t("shopReport.allMarketplaces")}>
            <button className="chip" aria-pressed={mp === "all"} onClick={() => setMp("all")}>
              {t("shopReport.allMarketplaces")} <span className="count">{rows.length}</span>
            </button>
            {marketplaces.map((m) => (
              <button key={m.code} className="chip" aria-pressed={mp === m.code} onClick={() => setMp(m.code)}>
                {mpName(m.code)} <span className="count">{rows.filter((r) => r.marketplace === m.code).length}</span>
              </button>
            ))}
          </div>
        )}
        <div className="chips" role="group" aria-label={t("shopReport.title")}>
          <button className="chip" aria-pressed={kind === "all"} onClick={() => setKind("all")}>
            {t("shopReport.all")} <span className="count">{inShop.length}</span>
          </button>
          {SHOP_DISCREPANCY_KINDS.filter((k) => countOf(k) > 0).map((k) => (
            <button key={k} className="chip" aria-pressed={kind === k} onClick={() => setKind(k)}>
              {tk("shopReport.kind", k)} <span className="count">{countOf(k)}</span>
            </button>
          ))}
        </div>

        {rows.length === 0
          ? <div className="banner banner-ok">{t("shopReport.clean")}</div>
          : shown.length === 0
            ? <p className="muted small">{t("shopReport.emptyFilter")}</p>
            : (
              <div className="list">
                {shown.map((r) => <Row key={`${r.kind}:${r.marketplace}:${r.externalId ?? r.item?.id}`} row={r} mpName={mpName} />)}
              </div>
            )}
      </section>
    </div>
  );
}
